// Damian Chatbot Service - Enhanced with Claude AI Integration
import { supabase } from '../lib/supabase';
import { inmateService } from './inmateService';
import { MIN_BAIL_THRESHOLD, getDynamicPhoneNumber, getDynamicPhoneText, getDynamicPhoneDisplay, getSessionId, isEligibleForBail, detectPainPoints, classifyIntent } from './constants';
import currentAgentService from './currentAgentService';
import claudeAIService from './claudeAIService';

export class DamianChatService {
  constructor() {
    this.sessionCounts = new Map();
  }

  // Get current dynamic phone numbers for responses
  async getDynamicPhoneData() {
    try {
      const [phoneNumber, phoneText, phoneDisplay] = await Promise.all([
        getDynamicPhoneNumber(),
        getDynamicPhoneText(), 
        getDynamicPhoneDisplay()
      ]);
      return { phoneNumber, phoneText, phoneDisplay };
    } catch (error) {
      return {
        phoneNumber: '+19168330052',
        phoneText: 'Call Duty Agent (916) 833-0052',
        phoneDisplay: '(916) 833-0052'
      };
    }
  }

  // Enhanced intent parsing with comprehensive pain-point detection - STRATEGY IMPLEMENTATION
  parseUserIntentWithPainPoints(message) {
    const lowerMessage = message?.toLowerCase();
    const painPoints = detectPainPoints(message);
    const intent = classifyIntent(message);

    // Enhanced search patterns
    if (this.isSearchRequest(lowerMessage)) {
      const params = this.extractSearchParams(message);
      return {
        type: 'SEARCH',
        params,
        painPoints,
        confidence: this.calculateSearchConfidence(params)
      };
    }

    // Naive/Illegal request detection - FROM STRATEGY F) SECTION
    const naiveOrIllegal = this.detectNaiveOrIllegalRequest(message);
    if (naiveOrIllegal) {
      return {
        type: naiveOrIllegal?.type === 'illegal' ? 'ILLEGAL_REQUEST' : 'NAIVE_REQUEST', 
        painPoints,
        requestType: naiveOrIllegal?.type,
        context: lowerMessage
      };
    }

    // Return classified intent with pain points - ENHANCED
    return {
      ...intent,
      painPoints,
      context: lowerMessage
    };
  }

  // NEW: Handle naive and illegal requests - STRATEGY REQUIREMENT F)
  detectNaiveOrIllegalRequest(message) {
    const lowerMessage = message?.toLowerCase();
    
    // Illegal patterns from strategy
    const illegalPatterns = [
      /hide.*from.*police/i,
      /evade.*law/i,
      /avoid.*arrest/i,
      /fake.*documents/i,
      /forged/i,
      /drugs/i,
      /weapons/i
    ];
    
    // Naive patterns from strategy  
    const naivePatterns = [
      /speeding.*ticket.*bail/i,
      /get.*out.*without.*paying/i,
      /free.*bail/i,
      /no.*cost.*bail/i
    ];
    
    const isIllegal = illegalPatterns?.some(pattern => pattern?.test(lowerMessage));
    const isNaive = naivePatterns?.some(pattern => pattern?.test(lowerMessage));
    
    if (isIllegal) {
      return { type: 'illegal' };
    }
    
    if (isNaive) {
      return { type: 'naive' };  
    }
    
    return null;
  }

  // NEW: Handle illegal requests with polite redirection - STRATEGY F)
  async handleIllegalRequests(claudeResponse = null) {
    const { phoneNumber } = await this.getDynamicPhoneData();
    
    if (claudeResponse && typeof claudeResponse === 'string') {
      return {
        reply: claudeResponse,
        actions: [{ type: 'CALL', phone: phoneNumber }]
      };
    }
    
    return {
      reply: `I can't help with that. My role's to walk you through safe, legal bail options. I'm here to help with legitimate bail bond services within Sacramento County.

**What I can help with:**
• Locating arrestees in Sacramento County jails
• Explaining the legal bail bond process
• Discussing payment options and plans
• Providing information about court requirements

How can I assist you with proper legal bail procedures?`,
      actions: [{ type: 'CALL', phone: phoneNumber }]
    };
  }

  // NEW: Handle naive requests with educational guidance - STRATEGY F)
  async handleNaiveRequests(claudeResponse = null) {
    const { phoneNumber } = await this.getDynamicPhoneData();
    
    if (claudeResponse && typeof claudeResponse === 'string') {
      return {
        reply: claudeResponse,
        actions: [
          { type: 'CTA', cta_type: 'learn_options', label: 'Learn About Bail Process' },
          { type: 'CALL', phone: phoneNumber }
        ]
      };
    }
    
    return {
      reply: `I understand there might be some confusion about how bail works. Let me clarify:

**How California Bail Actually Works:**
• Bail amounts are set by the court, not by us
• Bail bonds require a premium payment (10% of bail amount) 
• This premium is non-refundable but much less than full bail
• Payment plans and financing options may be available

**What's Not Possible:**
• Free bail or bonds without cost
• Getting someone out without any payment
• Avoiding the legal process

I can explain all your legal options and help you understand the real process. Can you provide more details about your specific situation?`,
      actions: [
        { type: 'CTA', cta_type: 'learn_options', label: 'Learn About Bail Process' },
        { type: 'CTA', cta_type: 'payment_options', label: 'Payment Options' },
        { type: 'CALL', phone: phoneNumber }
      ]
    };
  }

  // ✅ COMPLETELY REWRITTEN: Enhanced message processing with database-first Claude integration
  async processMessage(message, sessionId = null) {
    try {
      const sid = sessionId || getSessionId();
      const { phoneNumber, phoneText } = await this.getDynamicPhoneData();

      // Enhanced intent parsing with pain-point detection
      const intent = this.parseUserIntentWithPainPoints(message);

      // Track the interaction with enhanced analytics (for analytics only, not limiting)
      await this.trackChatInteraction(sid, message, intent);

      // Get agent context for Claude
      const agentInfo = await currentAgentService?.getCurrentAgent();
      const agentContext = {
        agentName: agentInfo?.full_name || 'Damian Sailors',
        agentPhone: await getDynamicPhoneDisplay(),
        hasEmergency: intent?.type === 'EMERGENCY',
        sessionContext: {
          sessionId: sid,
          painPoints: intent?.painPoints || [],
          intent: intent?.type
        }
      };

      // ✅ NEW: Database-first approach - get database results first
      let databaseResults = null;
      let searchPerformed = false;

      // If this is a search request, get database results first
      if (intent?.type === 'SEARCH' && intent?.params) {
        try {
          console.log('🔍 Performing database search for Claude AI context:', intent?.params);
          
          let searchResult = await Promise.race([
            inmateService?.flexibleSearch({
              firstName: intent?.params?.firstName || '',
              lastName: intent?.params?.lastName || '',
              dob: intent?.params?.dob || '',
              facility: intent?.params?.facility || '',
              bookingNumber: intent?.params?.bookingNumber || '',
              pageSize: 10 // Limit results for chat context
            }),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Database search timeout')), 15000)
            )
          ]);

          if (searchResult?.success && searchResult?.data) {
            databaseResults = searchResult?.data;
            searchPerformed = true;
            console.log('✅ Database results obtained:', databaseResults?.length, 'records');
          }
        } catch (error) {
          console.warn('Database search failed, proceeding with Claude fallback:', error);
          searchPerformed = true; // Mark as attempted
        }
      }

      // ✅ NEW: Enhanced Claude AI integration with database results
      let enhancedResponse = null;
      if (claudeAIService?.isConfigured()) {
        try {
          // Pass database results as structured facts to Claude
          enhancedResponse = await claudeAIService?.enhanceMessage(message, {
            ...agentContext,
            databaseResults: databaseResults, // ✅ CRITICAL: Pass DB results as structured facts
            searchPerformed: searchPerformed
          });
          
          // Validate Claude response quality
          if (enhancedResponse && typeof enhancedResponse === 'string' && enhancedResponse?.length > 20) {
            console.log('✅ Claude AI provided database-first response');
            
            // ✅ NEW: Return Claude response with database context
            return {
              reply: enhancedResponse,
              actions: this.generateActionsFromContext(databaseResults, phoneNumber),
              databaseResults: databaseResults,
              searchPerformed: searchPerformed
            };
          }
        } catch (error) {
          console.warn('Claude AI enhancement failed, using database-first fallback:', error);
        }
      }

      // ✅ NEW: Database-first fallback handling
      switch (intent?.type) {
        case 'SEARCH':
          return await this.handleDatabaseFirstSearch(intent?.params, sid, databaseResults, searchPerformed);

        case 'EMERGENCY':
          return await this.handleEmergencyWithCTAs(enhancedResponse);

        case 'PAYMENT_INQUIRY':
          return await this.handlePaymentInquiryWithPainPoints(intent?.painPoints, enhancedResponse);

        case 'OBJECTION_LESSON': // NEW: Specific handling for "teach them a lesson"
          return await this.handleTeachLessonObjection(enhancedResponse);

        case 'ILLEGAL_REQUEST': // NEW: Handle illegal requests
          return await this.handleIllegalRequests(enhancedResponse);

        case 'NAIVE_REQUEST': // NEW: Handle naive requests  
          return await this.handleNaiveRequests(enhancedResponse);

        case 'BAIL_INFO':
          return await this.handleBailInfoWithCTAs(intent?.context, enhancedResponse);

        case 'FACILITY_INFO':
          return await this.handleFacilityInfo(enhancedResponse);

        case 'GREETING':
          return await this.handleGreetingWithCTAs(enhancedResponse);

        default:
          return await this.handleDefaultWithEducationalCTAs(enhancedResponse, databaseResults);
      }
    } catch (error) {
      const { phoneNumber } = await this.getDynamicPhoneData();
      return {
        error: 'search_failed',
        reply: `I encountered an issue processing your request. Please try again or call us directly at ${await getDynamicPhoneDisplay()}.`,
        actions: [{ type: 'CALL', phone: phoneNumber }]
      };
    }
  }

  // Handle emergency situations with comprehensive CTAs and Claude AI enhancement
  async handleEmergencyWithCTAs(claudeResponse = null) {
    const { phoneNumber, phoneDisplay } = await this.getDynamicPhoneData();
    const agentName = await currentAgentService?.getCurrentAgentName();

    // Use Claude's response if available and appropriate
    if (claudeResponse && typeof claudeResponse === 'string' && claudeResponse?.length > 50) {
      return {
        reply: claudeResponse,
        actions: [
          { type: 'CALL', phone: phoneNumber, label: 'Emergency Call Now' }
        ]
      };
    }

    // Fallback to standard emergency response
    return {
      reply: `**🚨 URGENT BAIL ASSISTANCE ACTIVATED**

I understand this is an emergency situation. Here's what I can do right now:

**Immediate Actions:**
1. Search both facilities instantly for your person
2. Contact jail directly for status updates  
3. Begin paperwork preparation while we talk
4. Arrange 24/7 emergency processing

**Emergency Contact Methods:**
• **Call:** ${phoneDisplay} (answered 24/7)
• **Text:** ${phoneDisplay} (immediate response)
• **Priority Processing:** Available now

**Current Agent on Duty:** ${agentName}

**What I Need:**
• Full legal name (last name, first name)
• Approximate arrest date/time
• Any booking number if available

Please provide the arrestee's name and I'll begin the search immediately. Our emergency team is standing by.`,
      actions: [
        { type: 'CALL', phone: phoneNumber, label: 'Emergency Call Now' }
      ]
    };
  }

  // Handle payment inquiries with pain-point messaging and AI enhancement
  async handlePaymentInquiryWithPainPoints(painPoints = [], claudeResponse = null) {
    const { phoneNumber } = await this.getDynamicPhoneData();
    const hasMoneyWorries = painPoints?.includes('money_concerns');

    // Use Claude's response if it's contextually appropriate
    if (claudeResponse && typeof claudeResponse === 'string'&& claudeResponse?.includes('payment')) {
      return {
        reply: claudeResponse,
        actions: [
          { type: 'CTA', cta_type: 'payment_options', label: 'See Payment Options' },
          { type: 'CTA', cta_type: 'request_callback', label: 'Request Callback' },
          { type: 'CALL', phone: phoneNumber }
        ]
      };
    }

    if (hasMoneyWorries) {
      return {
        reply: `**Money's real — I hear you.** Sometimes we can structure payments or discuss collateral (case-by-case). Want me to outline what's possible?

**Bail Bond Fees & Payment Options:**

**Standard Rate:** 10% of total bail amount (California state maximum)
**Payment Plans:** Available with approved credit
**Accepted Methods:**
• Cash, certified check, money orders
• Credit/debit cards (Visa, MC, Discover, Amex)
• Wire transfers and bank transfers
• Collateral (property, vehicles, jewelry)

**Example:** $25,000 bail = $2,500 bond fee (non-refundable)

**Additional Services Included:**
• 24/7 processing and jail release
• Court date reminders and monitoring
• Warrant checks and legal referrals

I can provide an exact quote once we locate the arrestee. What's the person's name you're looking for?`,
        actions: [
          { type: 'CTA', cta_type: 'payment_options', label: 'See Payment Options' },
          { type: 'CTA', cta_type: 'request_callback', label: 'Request Callback' },
          { type: 'CALL', phone: phoneNumber }
        ]
      };
    }

    return await this.handleBailInfoWithCTAs('payment', claudeResponse);
  }

  // Handle "teach them a lesson" objection with AI enhancement
  async handleTeachLessonObjection(claudeResponse = null) {
    const { phoneNumber } = await this.getDynamicPhoneData();
    const agentName = await currentAgentService?.getCurrentAgentName();

    // Use Claude's response if it addresses the core concern appropriately
    if (claudeResponse && typeof claudeResponse === 'string'&& (claudeResponse?.toLowerCase()?.includes('accountability') || 
         claudeResponse?.toLowerCase()?.includes('lesson'))) {
      return {
        reply: claudeResponse,
        actions: [
          { type: 'CTA', cta_type: 'learn_options', label: 'Learn Your Options' },
          { type: 'CALL', phone: phoneNumber, label: `Talk to ${agentName}` }
        ]
      };
    }

    // Fallback to standard response
    return {
      reply: `**I hear you — wanting accountability makes sense.** A heads-up: jail isn't a structured program. Folks can pick up worse influences, connect with people who teach them how to dodge responsibility, or get exposed to drugs or threats. 

If you want a middle ground, we can set firm release conditions and clear expectations while keeping them safe and showing up to court. Want to talk it through?

**Consider This:**
• Jail can expose them to tougher influences and risky connections
• If you want accountability without that risk, we can set strict expectations
• Keep them on track with court dates and house rules
• Posting bail doesn't remove court accountability — it just means they face it outside jail

**What We Can Help With:**
• Setting clear house rules and expectations
• Court date reminders so dates aren't missed
• Check-ins and compliance monitoring`,
      actions: [
        { type: 'CTA', cta_type: 'learn_options', label: 'Learn Your Options' },
        { type: 'CALL', phone: phoneNumber, label: `Talk to ${agentName}` }
      ]
    };
  }

  // ✅ NEW: Database-first search handling with structured fact presentation
  async handleDatabaseFirstSearch(params, sessionId, databaseResults = null, searchPerformed = false) {
    const { phoneNumber, phoneText, phoneDisplay } = await this.getDynamicPhoneData();

    // If we already have database results, use them
    if (databaseResults && Array.isArray(databaseResults)) {
      // Keep search session tracking for analytics only (not for limiting)
      await this.trackSearchSession(sessionId, this.formatSearchQuery(params), databaseResults);

      if (databaseResults?.length === 0) {
        return await this.handleDatabaseFirstNoResults(params, searchPerformed);
      }

      // Convert search results to display format
      const displayRows = this.convertToDisplayFormat(databaseResults);
      
      return await this.handleDatabaseFirstResults(displayRows, params, sessionId);
    }

    // If search wasn't performed yet, perform it now
    if (!searchPerformed) {
      try {
        let searchResult = await Promise.race([
          inmateService?.flexibleSearch({
            firstName: params?.firstName || '',
            lastName: params?.lastName || '',
            dob: params?.dob || '',
            facility: params?.facility || '',
            bookingNumber: params?.bookingNumber || '',
            pageSize: 10
          }),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Search timeout')), 15000)
          )
        ]);

        if (searchResult?.success) {
          await this.trackSearchSession(sessionId, this.formatSearchQuery(params), searchResult?.data || []);
          
          if (!searchResult?.data?.length) {
            return await this.handleDatabaseFirstNoResults(params, true);
          }

          const displayRows = this.convertToDisplayFormat(searchResult?.data);
          return await this.handleDatabaseFirstResults(displayRows, params, sessionId);
        }
      } catch (error) {
        console.error('Database search error:', error);
      }
    }

    // Database unavailable - provide database-first error response
    return {
      reply: `I'm experiencing connectivity issues with our jail database system right now. This could be due to:\n\n• Database maintenance in progress\n• Network connectivity issues\n• High system load\n\nI've attempted to connect to our Sacramento County records system. Please wait a moment and try again, or call us directly at ${phoneText} for immediate assistance with locating inmates.`,
      actions: [
        { type: 'RETRY_SEARCH', label: 'Try Search Again' },
        { type: 'CALL', phone: phoneNumber }
      ],
      error: 'database_connectivity_failed'
    };
  }

  // ✅ NEW: Handle database results with structured presentation
  async handleDatabaseFirstResults(displayRows, originalParams, sessionId) {
    const { phoneNumber } = await this.getDynamicPhoneData();
    
    if (displayRows?.length <= 5) {
      const threshold = MIN_BAIL_THRESHOLD;
      const eligibleIds = displayRows
        ?.filter(row => isEligibleForBail(row, threshold))
        ?.map(row => row?.person_id);

      const actions = [
        { type: 'SEARCH_RESULTS_TABLE', rows: displayRows, sessionId: sessionId }
      ];

      // ✅ NEW: Database-first structured response
      let replyMessage = `✅ I found ${displayRows?.length} person${displayRows?.length !== 1 ? 's' : ''} in Sacramento County:\n\n`;
      
      // Add structured table format
      replyMessage += '**CURRENT CUSTODY INFORMATION:**\n\n';
      displayRows?.forEach((row, index) => {
        replyMessage += `**${index + 1}. ${row?.first_name} ${row?.middle_name || ''} ${row?.last_name}**\n`;
        if (row?.dob) replyMessage += `DOB: ${row?.dob}\n`;
        replyMessage += `Status: ${row?.current_status}\n`;
        if (row?.facility_display) replyMessage += `Facility: ${row?.facility_display}\n`;
        if (row?.bail_amount) replyMessage += `Bail: $${row?.bail_amount?.toLocaleString()}\n`;
        if (row?.charges?.length > 0) replyMessage += `Charges: ${row?.charges?.join(', ')}\n`;
        replyMessage += '\n';
      });

      if (eligibleIds?.length > 0) {
        replyMessage += `**Next Steps for Bail Process:**\n`;
        replyMessage += `• California bail bonds are 10% of the court-set bail amount\n`;
        replyMessage += `• I can begin processing immediately for eligible individuals\n`;
        replyMessage += `• Payment plans and financing options available\n\n`;
        replyMessage += `Which person matches who you're looking for?`;
        
        actions?.push({ 
          type: 'SELECTION_PROMPT', 
          eligible_ids: eligibleIds, 
          threshold,
          message: 'Select the person to begin bail process'
        });
      } else {
        replyMessage += `These individuals have been processed through our system. I can provide additional details about their cases.`;
      }

      actions?.push({ type: 'CALL', phone: phoneNumber });

      return {
        reply: replyMessage,
        actions,
        databaseResults: displayRows
      };
    }

    // Too many results - ask for refinement
    return {
      reply: `I found ${displayRows?.length} people matching your search. To provide the most accurate information, please provide more specific details:\n\n**Be more specific:**\n• Add a last name if you only provided first name\n• Include date of birth (MM/DD/YYYY) if known\n• Specify facility (Sacramento Main Jail or Rio Cosumnes)\n• Add middle name or initial if known\n\nThis will help me identify the exact person in our database.`,
      actions: [
        { type: 'CTA', cta_type: 'refine_search', label: 'Refine Search' },
        { type: 'SHOW_PARTIAL_RESULTS', results: displayRows?.slice(0, 3), label: 'Show Top 3 Results' },
        { type: 'CALL', phone: phoneNumber }
      ],
      searchContext: {
        tooManyResults: true,
        suggestedAction: 'refine_search_criteria',
        originalQuery: originalParams,
        resultCount: displayRows?.length
      }
    };
  }

  // ✅ NEW: Handle no database results with database-first messaging
  async handleDatabaseFirstNoResults(params, searchPerformed) {
    const { phoneNumber, phoneText } = await this.getDynamicPhoneData();
    const searchedName = this.getSearchedName(params);

    // ✅ CRITICAL: No external website redirects - database-first response only
    return {
      reply: `I wasn't able to find "${searchedName}" in my system. Here are the possible reasons:\n\n**Database Status:**\n• Still being processed (can take 2-4 hours after arrest)\n• May be at a facility outside Sacramento County\n• Name spelling might be different than expected\n• Person may have been released recently\n\n**Next Steps:**\n• I can try different name spellings or variations\n• Search with partial information (just first or last name)\n• Check again in a few hours if arrest was recent\n\n**Would you like me to connect you with Damian directly at ${phoneText}?**`,
      actions: [
        { type: 'CTA', cta_type: 'try_different_spelling', label: 'Try Different Spelling' },
        { type: 'CTA', cta_type: 'try_partial_name', label: 'Try Partial Name' },
        { type: 'CTA', cta_type: 'request_callback', label: 'Request Callback' },
        { type: 'CALL', phone: phoneNumber }
      ],
      databaseResults: []
    };
  }

  // ✅ NEW: Generate actions based on database context
  generateActionsFromContext(databaseResults, phoneNumber) {
    const actions = [];
    
    if (databaseResults && databaseResults?.length > 0) {
      // Results found - offer selection and bail process
      actions?.push({ type: 'CTA', cta_type: 'select_person', label: 'Select Person' });
      actions?.push({ type: 'CTA', cta_type: 'start_bail_process', label: 'Start Bail Process' });
    } else {
      // No results - offer search refinement
      actions?.push({ type: 'CTA', cta_type: 'refine_search', label: 'Try Different Search' });
      actions?.push({ type: 'CTA', cta_type: 'request_callback', label: 'Request Callback' });
    }
    
    // Always offer direct contact
    actions?.push({ type: 'CALL', phone: phoneNumber });
    
    return actions;
  }

  // ✅ ENHANCED: Default response with database-first approach
  async handleDefaultWithEducationalCTAs(enhancedResponse = null, databaseResults = null) {
    const { phoneNumber, phoneDisplay } = await this.getDynamicPhoneData();

    // Use Claude's response if it's comprehensive and database-aware
    if (enhancedResponse && typeof enhancedResponse === 'string' && enhancedResponse?.length > 100) {
      return {
        reply: enhancedResponse,
        actions: [
          { type: 'CTA', cta_type: 'learn_options', label: 'Learn Your Options' },
          { type: 'CTA', cta_type: 'payment_options', label: 'See Payment Options' },
          { type: 'CALL', phone: phoneNumber }
        ],
        databaseResults: databaseResults
      };
    }

    return {
      reply: `I'm here to help with inmate searches and bail bonds in Sacramento County using our comprehensive database system.\n\n**🔍 What I Can Search:**\n• Sacramento County Main Jail records\n• Rio Cosumnes Correctional Facility records\n• Real-time booking and release information\n• Charges, bail amounts, and court dates\n\n**💼 Bail Bond Services:**\n• Calculate exact bail bond fees (10% of bail amount)\n• Explain payment options and financing\n• Process emergency releases 24/7\n• Handle all paperwork and court requirements\n\n**How can I help you today?**\nJust provide a name to search (last, first), ask about the bail process, or call directly at ${phoneDisplay}.`,
      actions: [
        { type: 'CTA', cta_type: 'start_search', label: 'Search for Someone' },
        { type: 'CTA', cta_type: 'learn_options', label: 'Learn About Bail Process' },
        { type: 'CTA', cta_type: 'payment_options', label: 'Payment Options' },
        { type: 'CALL', phone: phoneNumber }
      ]
    };
  }

  // Enhanced search handling with intelligent conversational responses - UNLIMITED SEARCHES
  async handleSearch(params, sessionId, claudeResponse = null) {
    try {
      const { phoneNumber, phoneText, phoneDisplay } = await this.getDynamicPhoneData();

      // Enhanced database connection checking and retry logic
      let searchResult;
      let retryCount = 0;
      const maxRetries = 3;

      while (retryCount < maxRetries) {
        try {
          // Perform search using existing inmate service with timeout
          searchResult = await Promise.race([
            inmateService?.flexibleSearch({
              firstName: params?.firstName || '',
              lastName: params?.lastName || '',
              dob: params?.dob || '',
              facility: params?.facility || '',
              bookingNumber: params?.bookingNumber || '',
              pageSize: 10 // Limit results for chat context
            }),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Search timeout')), 15000)
            )
          ]);

          // If search was successful, break out of retry loop
          if (searchResult?.success || searchResult?.data) {
            break;
          }
        } catch (error) {
          retryCount++;
          console.warn(`Search attempt ${retryCount} failed:`, error?.message);
          
          // Wait before retry (exponential backoff)
          if (retryCount < maxRetries) {
            await new Promise(resolve => setTimeout(resolve, 1000 * retryCount));
          }
        }
      }

      // Keep search session tracking for analytics only (not for limiting)
      await this.trackSearchSession(sessionId, this.formatSearchQuery(params), searchResult?.data || []);

      // Enhanced error handling with specific database connectivity messages
      if (!searchResult?.success && retryCount >= maxRetries) {
        return {
          reply: `I'm experiencing connectivity issues with the jail database system right now. This could be due to:\n\n• Database maintenance in progress\n• Network connectivity issues\n• High system load from multiple searches\n\nI've attempted to reconnect ${maxRetries} times. Please wait a moment and try again, or call us directly at ${phoneText} for immediate assistance.`,
          actions: [
            { type: 'RETRY_SEARCH', label: 'Try Search Again' },
            { type: 'CALL', phone: phoneNumber }
          ],
          error: 'database_connectivity_failed'
        };
      }

      // Enhanced no results handling with intelligent suggestions
      if (!searchResult?.data?.length) {
        return await this.handleNoResultsConversationally(params, null, claudeResponse);
      }

      // Convert search results to display format
      const displayRows = this.convertToDisplayFormat(searchResult?.data);
      
      // ENHANCED: Conversational result handling with Claude AI - UNLIMITED SEARCHES
      return await this.handleResultsConversationally(displayRows, params, null, sessionId, claudeResponse);

    } catch (error) {
      console.error('Database search error:', error);
      const { phoneNumber, phoneText } = await this.getDynamicPhoneData();
      
      return {
        reply: `I encountered a system error while searching the database. This might be a temporary problem with our database connection.\n\nError details: ${error?.message || 'Unknown error'}\n\nPlease try again in a moment or call us directly at ${phoneText} for immediate assistance.`,
        actions: [
          { type: 'RETRY_SEARCH', label: 'Try Again' },
          { type: 'CALL', phone: phoneNumber }
        ],
        error: 'search_system_error'
      };
    }
  }

  // NEW: Intelligent conversational handling for search results - UNLIMITED SEARCHES
  async handleResultsConversationally(displayRows, originalParams, sessionStatus, sessionId, claudeResponse = null) {
    const { phoneNumber } = await this.getDynamicPhoneData();
    
    // Scenario 1: Too many "John" results - ask for last name
    if (this.shouldAskForLastName(displayRows, originalParams)) {
      const firstName = originalParams?.firstName || 'that name';
      const count = displayRows?.length;
      
      return {
        reply: `I found ${count} people named "${firstName}" in the system. That's quite a few! To help narrow this down, could you provide a **last name**? 

Even just the first few letters would help, like "Smi" for Smith or "Gar" for Garcia.

**Examples:**
• "Find John Smith" •"Look for John Gar" (partial last name)
• "Search for John S" (just first letter)

This will help me find the exact person you're looking for.`,
        actions: [
          { type: 'CTA', cta_type: 'refine_search', label: 'Provide Last Name' },
          { type: 'SHOW_ALL_RESULTS', results: displayRows?.slice(0, 5), label: 'Show Top 5 Results' },
          { type: 'CALL', phone: phoneNumber }
        ],
        searchContext: {
          tooManyResults: true,
          suggestedAction: 'provide_last_name',
          originalQuery: originalParams,
          resultCount: count
        }
      };
    }

    // Scenario 2: Unusual name with no results - check spelling
    if (this.shouldCheckSpelling(displayRows, originalParams)) {
      const searchedName = this.getSearchedName(originalParams);
      
      return {
        reply: `I couldn't find anyone named "${searchedName}" in our current database. Since that's not a common name, let me help you:

**Is this the correct spelling?** Unusual names are often spelled differently than expected.

**Do they go by a different name?** They might use:
• A more common nickname
• Their middle name instead  
• A shortened or Americanized version
• A previous married name

**Other possibilities:**
• They haven't been processed yet (can take 2-4 hours after arrest)
• They're at a different facility outside Sacramento County
• The name might have been recorded differently

What other name variations should I try? Even a close spelling might help me find them.`,
        actions: [
          { type: 'CTA', cta_type: 'try_different_spelling', label: 'Try Different Spelling' },
          { type: 'CTA', cta_type: 'try_common_nickname', label: 'Try Common Nickname' },
          { type: 'CTA', cta_type: 'request_callback', label: 'Request Callback' },
          { type: 'CALL', phone: phoneNumber }
        ]
      };
    }

    // Scenario 3: Reasonable number of results - show them
    if (displayRows?.length <= 5) {
      const threshold = MIN_BAIL_THRESHOLD;
      const eligibleIds = displayRows
        ?.filter(row => isEligibleForBail(row, threshold))
        ?.map(row => row?.person_id);

      const actions = [
        { type: 'SEARCH_RESULTS_TABLE', rows: displayRows, sessionId: sessionId }
      ];

      let replyMessage = `I found ${displayRows?.length} person${displayRows?.length !== 1 ? 's' : ''} matching your search:\n\n`;

      if (eligibleIds?.length > 0) {
        replyMessage += `**If you see the person you're looking for, let me know and I can help with the bail process.**\n\n`;
        
        actions?.push({ 
          type: 'SELECTION_PROMPT', 
          eligible_ids: eligibleIds, 
          threshold,
          message: `If you see the person, check the box next to their name. If you don't see the right person, I can search again with different information.`
        });

        actions?.push({ type: 'CTA', cta_type: 'continue_search', label: 'Search Again' });
      } else {
        replyMessage += `These individuals have been processed through our system. I can provide more details about their cases or help you search for different people.\n\n`;
        actions?.push({ type: 'CTA', cta_type: 'continue_search', label: 'Search Again' });
      }

      actions?.push({ type: 'CALL', phone: phoneNumber });

      return {
        reply: replyMessage,
        actions
      };
    }

    // Scenario 4: Too many results but not just a first name - suggest refinement
    return {
      reply: `I found ${displayRows?.length} people matching your search criteria. That's quite a lot! To get better results, try:

**Be more specific:**
• Add a last name if you only searched first name
• Include date of birth if you know it (MM/DD/YYYY)
• Add middle name or initial
• Specify which facility (Sacramento Main Jail or Rio Cosumnes)

**Examples:**
• "Find John Smith born 01/15/1990" •"Look for Maria Garcia at Sacramento Main" •"Search for Robert J. Johnson"

This will help me narrow down to the exact person you need.`,
      actions: [
        { type: 'CTA', cta_type: 'refine_search', label: 'Refine Search' },
        { type: 'SHOW_PARTIAL_RESULTS', results: displayRows?.slice(0, 3), label: 'Show Top 3 Results' },
        { type: 'CALL', phone: phoneNumber }
      ],
      searchContext: {
        tooManyResults: true,
        suggestedAction: 'refine_search_criteria',
        originalQuery: originalParams,
        resultCount: displayRows?.length
      }
    };
  }

  // NEW: Enhanced no results handling with conversational suggestions and AI - UNLIMITED SEARCHES
  async handleNoResultsConversationally(params, sessionStatus, claudeResponse = null) {
    const { phoneNumber, phoneText } = await this.getDynamicPhoneData();
    const searchedName = this.getSearchedName(params);

    // Use Claude's response if it's contextually appropriate
    if (claudeResponse && typeof claudeResponse === 'string'&& claudeResponse?.toLowerCase()?.includes('not found')) {
      return {
        reply: claudeResponse,
        actions: [
          { type: 'CTA', cta_type: 'try_different_spelling', label: 'Try Different Spelling' },
          { type: 'CTA', cta_type: 'try_common_nickname', label: 'Try Common Nickname' },
          { type: 'CTA', cta_type: 'request_callback', label: 'Request Callback' },
          { type: 'CALL', phone: phoneNumber }
        ]
      };
    }

    // Check if this looks like an unusual name
    if (this.isUnusualName(searchedName)) {
      return {
        reply: `I couldn't find anyone named "${searchedName}" in our current database. Since "${searchedName}" is not a very common name, let me ask:

**Is this the correct spelling?** Unusual names are often spelled differently than expected.

**Do they go by a different name?** They might use:
• A more common nickname
• Their middle name instead  
• A shortened or Americanized version
• A previous married name

**Other possibilities:**
• They haven't been processed yet (can take 2-4 hours after arrest)
• They're at a different facility outside Sacramento County
• The name might have been recorded differently

What other name variations should I try? Even a close spelling might help me find them.`,
        actions: [
          { type: 'CTA', cta_type: 'try_different_spelling', label: 'Try Different Spelling' },
          { type: 'CTA', cta_type: 'try_common_nickname', label: 'Try Common Nickname' },
          { type: 'CTA', cta_type: 'request_callback', label: 'Request Callback' },
          { type: 'CALL', phone: phoneNumber }
        ]
      };
    }

    // Standard no results response for common names
    const searchTerms = [];
    if (params?.firstName || params?.lastName) {
      searchTerms?.push(`${params?.lastName || ''}, ${params?.firstName || ''}`?.trim());
    }
    if (params?.bookingNumber) {
      searchTerms?.push(`booking #${params?.bookingNumber}`);
    }

    const searchText = searchTerms?.length > 0 ? ` for "${searchTerms?.join(', ')}"` : '';
    
    return {
      reply: `I couldn't find any matches${searchText} in our current database. This could mean:

• **Still being processed** - Can take 2-4 hours after arrest
• **Different facility** - They might be outside Sacramento County  
• **Name spelling variation** - Try different spellings or nicknames
• **Already released** - They may have been released recently

**Let me help you try other approaches:**
• Try a nickname (like "Mike" for "Michael")
• Check spelling variations
• Try just the last name if you searched both
• Include middle name or initial if you know it

${phoneText} for immediate assistance.`,
      actions: [
        { type: 'CTA', cta_type: 'try_nickname', label: 'Try Nickname' },
        { type: 'CTA', cta_type: 'try_different_spelling', label: 'Try Different Spelling' },
        { type: 'CTA', cta_type: 'continue_search', label: 'Search Again' },
        { type: 'CALL', phone: phoneNumber }
      ]
    };
  }

  // NEW: Helper methods for intelligent decision making
  shouldAskForLastName(results, params) {
    return results?.length > 5 && 
           params?.firstName && 
           !params?.lastName && 
           this.isCommonFirstName(params?.firstName);
  }

  shouldCheckSpelling(results, params) {
    const searchedName = this.getSearchedName(params);
    return results?.length === 0 && 
           this.isUnusualName(searchedName) && 
           searchedName?.length > 5;
  }

  isCommonFirstName(firstName) {
    const commonNames = [
      'john', 'james', 'robert', 'michael', 'william', 'david', 'richard', 'joseph',
      'thomas', 'christopher', 'charles', 'daniel', 'matthew', 'anthony', 'mark',
      'donald', 'steven', 'paul', 'andrew', 'joshua', 'kenneth', 'kevin', 'brian',
      'george', 'edward', 'ronald', 'timothy', 'jason', 'jeffrey', 'ryan',
      'mary', 'patricia', 'jennifer', 'linda', 'elizabeth', 'barbara', 'susan',
      'jessica', 'sarah', 'karen', 'nancy', 'lisa', 'betty', 'helen', 'sandra',
      'donna', 'carol', 'ruth', 'sharon', 'michelle', 'laura', 'sarah', 'kimberly',
      'deborah', 'dorothy', 'lisa', 'nancy', 'karen', 'betty', 'helen'
    ];
    return commonNames?.includes(firstName?.toLowerCase());
  }

  isUnusualName(name) {
    if (!name || name?.length < 4) return false;
    
    const hasNumbers = /\d/?.test(name);
    const hasSpecialChars = /[^a-zA-Z\s]/?.test(name);
    const consonantClusters = /[bcdfghjklmnpqrstvwxyz]{4,}/i?.test(name);
    const unusualCombos = /[qx][^u]|[zx][aeiou]/i?.test(name);
    
    return hasNumbers || hasSpecialChars || consonantClusters || unusualCombos;
  }

  getSearchedName(params) {
    const parts = [];
    if (params?.firstName) parts?.push(params?.firstName);
    if (params?.lastName) parts?.push(params?.lastName);
    return parts?.join(' ') || params?.bookingNumber || 'the person';
  }

  // Enhanced greeting with comprehensive CTAs and AI enhancement
  async handleGreetingWithCTAs(claudeResponse = null) {
    const { phoneNumber, phoneDisplay } = await this.getDynamicPhoneData();
    const agentName = await currentAgentService?.getCurrentAgentName();
    const currentAgent = await currentAgentService?.getCurrentAgent();

    // Use Claude's response if it's a proper greeting/introduction
    if (claudeResponse && typeof claudeResponse === 'string'&& claudeResponse?.toLowerCase()?.includes('help')) {
      return {
        reply: claudeResponse,
        actions: [
          { type: 'CTA', cta_type: 'start_search', label: 'Search for Someone' },
          { type: 'CTA', cta_type: 'learn_options', label: 'Learn About Bail Process' },
          { type: 'CALL', phone: phoneNumber }
        ]
      };
    }

    return {
      reply: `**Hello! I'm ${agentName}, your licensed California bail bond agent${currentAgent?.license_number ? ` (License #${currentAgent?.license_number})` : ''}.** I'm here to help you 24/7 with:

**🔍 Instant Arrestee Search**
• Sacramento County Main Jail
• Rio Cosumnes Correctional Facility  
• Real-time booking information

**💰 Bail Bond Services**
• Immediate bail assessment
• Payment plans available
• 10% fee (state maximum)
• All major credit cards accepted

**⚡ Emergency Services**
• 24/7 jail release processing
• Court date reminders
• Immigration bonds
• Warrant assistance

**How can I help you right now?**
Just tell me the person's name you're looking for (last name, first name), or ask me anything about the bail process. I have immediate access to both Sacramento County jail databases.

You can also call me directly at ${phoneDisplay} for immediate assistance.`,
      actions: [
        { type: 'CTA', cta_type: 'start_search', label: 'Search for Someone' },
        { type: 'CTA', cta_type: 'learn_options', label: 'Learn About Bail Process' },
        { type: 'CALL', phone: phoneNumber }
      ]
    };
  }

  // Enhanced default response with educational mini-cards and AI
  async handleDefaultWithEducationalCTAs(claudeResponse = null) {
    const { phoneNumber, phoneDisplay } = await this.getDynamicPhoneData();

    // Use Claude's response if it's comprehensive and helpful
    if (claudeResponse && typeof claudeResponse === 'string' && claudeResponse?.length > 100) {
      return {
        reply: claudeResponse,
        actions: [
          { type: 'CTA', cta_type: 'learn_options', label: 'Learn Your Options' },
          { type: 'CTA', cta_type: 'payment_options', label: 'See Payment Options' },
          { type: 'CALL', phone: phoneNumber }
        ]
      };
    }

    return {
      reply: `I'm here to help with inmate searches and bail bonds in Sacramento County. I can:

**🔍 Search Services:**
• Find inmates by name, booking number, or charges
• Check Sacramento County Main Jail database  
• Access Rio Cosumnes Correctional Facility records
• Provide real-time booking information

**💼 Bail Bond Services:**
• Calculate exact bail bond fees (10% of bail amount)
• Explain payment options and plans
• Process emergency releases 24/7
• Handle all paperwork and court requirements

**Common Questions I Can Answer:**
• "Search for Smith, John"
• "What does a $25,000 bail cost?" •"How long does release take?" •"What payment methods do you accept?"

What specific information do you need? You can search for someone by name (last, first), ask about the bail process, or call me directly at ${phoneDisplay}.`,
      actions: [
        { type: 'CTA', cta_type: 'learn_options', label: 'Learn Your Options' },
        { type: 'CTA', cta_type: 'payment_options', label: 'See Payment Options' },
        { type: 'CALL', phone: phoneNumber }
      ]
    };
  }

  // Enhanced bail info with comprehensive CTAs and AI
  async handleBailInfoWithCTAs(context, claudeResponse = null) {
    if (context?.includes('cost') || context?.includes('fee') || context?.includes('payment')) {
      return await this.handlePaymentInquiryWithPainPoints([], claudeResponse);
    }

    const { phoneNumber } = await this.getDynamicPhoneData();

    // Use Claude's response if it explains the bail process comprehensively
    if (claudeResponse && typeof claudeResponse === 'string'&& (claudeResponse?.toLowerCase()?.includes('bail') && claudeResponse?.toLowerCase()?.includes('process'))) {
      return {
        reply: claudeResponse,
        actions: [
          { type: 'CTA', cta_type: 'start_search', label: 'Search for Someone' },
          { type: 'CTA', cta_type: 'payment_options', label: 'Payment Options' },
          { type: 'CALL', phone: phoneNumber }
        ]
      };
    }

    return {
      reply: `Here's how the bail bond process works:

**Important: California Bail Information**
In California, bail is an insurance product. The premium that we need to bail out a person from custody is a percentage of the bail.

**Step 1: Locate the Arrestee**
I'll search our real-time database covering Sacramento County Main Jail and Rio Cosumnes Correctional Facility.

**Step 2: Bail Assessment**  
I'll review the charges, bail amount set by the court, and eligibility requirements.

**Step 3: Payment & Paperwork**
• Complete bond application
• Arrange payment (10% of bail amount)
• Process collateral if needed
• Submit to jail for release

**Step 4: Release & Monitoring**
• Release typically takes 2-6 hours after processing
• Court date reminders provided
• Compliance monitoring included

**Timeline:** Most releases occur within 2-6 hours once paperwork is submitted.

Would you like me to search for someone specific or explain payment options in detail?`,
      actions: [
        { type: 'CTA', cta_type: 'start_search', label: 'Search for Someone' },
        { type: 'CTA', cta_type: 'payment_options', label: 'Payment Options' },
        { type: 'CALL', phone: phoneNumber }
      ]
    };
  }

  // Enhanced no results response with supportive messaging and search count
  async generateNoResultsResponseWithSearchCount(params, remainingSearches) {
    const { phoneText } = await this.getDynamicPhoneData();
    const searchTerms = [];
    if (params?.firstName || params?.lastName) {
      searchTerms?.push(`${params?.lastName || ''}, ${params?.firstName || ''}`?.trim());
    }
    if (params?.bookingNumber) {
      searchTerms?.push(`booking #${params?.bookingNumber}`);
    }

    const searchText = searchTerms?.length > 0 ? ` for "${searchTerms?.join(', ')}"` : '';
    const remainingText = remainingSearches > 0 
      ? `\n\nYou have ${remainingSearches} search${remainingSearches !== 1 ? 'es' : ''} remaining in this session.` 
      : `\n\nYou've used all 5 searches for this session. For additional searches, please visit http://www.sacsheriff.com`;

    return `I couldn't find any matches${searchText} in our current database. This could mean:

• The person hasn't been processed yet (can take 2-4 hours after arrest)
• They may be held at a different facility outside our system
• The name spelling might be different than expected
• They may have already been released

**Let me help you in other ways:**
• I can check with different name variations or spelling
• We can search using partial information
• I can provide information about the booking process

${phoneText} for immediate assistance, or provide any additional details you might have.${remainingText}`;
  }

  // Enhanced search results with comprehensive messaging and selection UI
  generateSearchResultsResponseWithSelection(rows, eligibleCount, remainingSearches) {
    const baseResponse = `Here are the search results I found (${rows?.length} record${rows?.length !== 1 ? 's' : ''}):\n\n`;

    if (eligibleCount > 0) {
      const selectionMessage = `**If you see the person, check the box next to the name. If you don't see the person, I can keep searching if you have different information.**\n\n`;

      const remainingMessage = remainingSearches > 0 
        ? `You have ${remainingSearches} search${remainingSearches !== 1 ? 'es' : ''} remaining in this session.`
        : `You've used all 5 searches for this session. For additional help, please visit http://www.sacsheriff.com`;

      return baseResponse + selectionMessage + remainingMessage;
    } else {
      return baseResponse + `The individuals shown ${rows?.length > 1 ? 'have' : 'has'} been processed through our system. I can provide additional details about their cases or help you search for different individuals.`;
    }
  }

  // Handle selection feedback
  async handleRecordSelection(selectedCount, totalCount) {
    try {
      let response;

      if (selectedCount === 0) {
        response = "I see you haven't selected anyone yet. Take your time reviewing the results. If you need help finding the right person, I'm here to assist with additional search options.";
      } else if (selectedCount === 1) {
        response = "Great! You've selected one person. If you need more information about this individual or want to continue with your inquiry, let me know how I can help.";
      } else {
        response = `You've selected ${selectedCount} people. If these are the individuals you're looking for, I can help you with next steps. If you need to refine your selection, feel free to adjust your choices.`;
      }

      // Track the selection interaction
      await supabase?.rpc('track_chat_interaction', {
        lead_id: null,
        interaction_type: 'record_selection',
        user_message: `Selected ${selectedCount} out of ${totalCount} records`,
        bot_response: response,
        context: {
          selectedCount,
          totalCount,
          timestamp: new Date()?.toISOString()
        }
      });

      return {
        success: true,
        response
      };
    } catch (error) {
      console.warn('Error handling record selection:', error);
      return {
        success: false,
        response: selectedCount > 0 ? 
          "I see you've made your selection. How can I help you next?" : "Let me know if you need help finding the right person."
      };
    }
  }

  // Track chat interactions for comprehensive analytics
  async trackChatInteraction(sessionId, message, intent) {
    try {
      const interactionData = {
        session_id: sessionId,
        message_type: 'user_message',
        content: message,
        pain_point_detected: intent?.painPoints?.join(',') || null,
        metadata: {
          intent_type: intent?.type,
          confidence: intent?.confidence || 0,
          pain_points: intent?.painPoints || []
        }
      };

      // Store in database for analytics
      await supabase?.from('chat_interactions')?.insert([interactionData]);
    } catch (error) {
      // Silent fail for analytics - don't break chat flow console.log('Analytics tracking error:', error);
    }
  }

  // Existing methods (unchanged for compatibility)
  isSearchRequest(message) {
    const searchIndicators = [
      'search', 'find', 'look for', 'lookup', 'arrestee', 'inmate',
      'booking', 'arrested', 'jail', 'custody', 'detained'
    ];

    return searchIndicators?.some(indicator => message?.includes(indicator));
  }

  extractSearchParams(message) {
    const params = {};

    // Name extraction patterns
    const namePatterns = [
      /(?:name|called)\s+(?:is\s+)?([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/i,
      /(?:for\s+|find\s+)([A-Z][a-z]+(?:\s+[A-Z][a-z]+)*)/i,
      /([A-Z][a-z]+\s+[A-Z][a-z]+)/g
    ];

    for (const pattern of namePatterns) {
      const match = message?.match(pattern);
      if (match) {
        const fullName = match?.[1]?.trim();
        const nameParts = fullName?.split(/\s+/);

        if (nameParts?.length >= 2) {
          params.firstName = nameParts?.[0];
          params.lastName = nameParts?.[nameParts?.length - 1];
          if (nameParts?.length > 2) {
            params.middleName = nameParts?.slice(1, -1)?.join(' ');
          }
        }
        break;
      }
    }

    // DOB extraction
    const dobPattern = /(\d{1,2}\/\d{1,2}\/\d{2,4}|\d{4}-\d{1,2}-\d{1,2})/;
    const dobMatch = message?.match(dobPattern);
    if (dobMatch) {
      params.dob = dobMatch?.[1];
    }

    // Booking number extraction
    const bookingPattern = /(?:booking|book(?:ed)?|#)\s*(\w+\d+|\d+\w*)/i;
    const bookingMatch = message?.match(bookingPattern);
    if (bookingMatch) {
      params.bookingNumber = bookingMatch?.[1];
    }

    // Facility extraction
    if (message?.toLowerCase()?.includes('main jail') || message?.toLowerCase()?.includes('sacramento county')) {
      params.facility = 'SAC_MAIN';
    } else if (message?.toLowerCase()?.includes('rio') || message?.toLowerCase()?.includes('cosumnes')) {
      params.facility = 'SAC_RIO';
    }

    return params;
  }

  calculateSearchConfidence(params) {
    let score = 0;
    if (params?.firstName && params?.lastName) score += 0.8;
    else if (params?.firstName || params?.lastName) score += 0.5;
    if (params?.dob) score += 0.3;
    if (params?.bookingNumber) score += 0.9;
    if (params?.facility) score += 0.2;

    return Math.min(score, 1.0);
  }

  convertToDisplayFormat(records) {
    return records?.map(record => ({
      person_id: record?.person_id || `p_${Date.now()}_${Math.random()?.toString(36)?.slice(2)}`,
      first_name: record?.first_name || 'UNKNOWN',
      middle_name: record?.middle_name || null,
      last_name: record?.last_name || 'UNKNOWN',
      dob: record?.date_of_birth || record?.dob || null,
      current_status: this.mapStatus(record?.status),
      facility: record?.facility_id || null,
      facility_display: record?.facility_name || record?.facility_id || 'Unknown Facility',
      bail_amount: record?.total_bail || null,
      last_arrest_datetime: record?.arrest_datetime || null,
      last_release_datetime: record?.release_datetime || null,
      charges: Array.isArray(record?.charges) ? record?.charges : [record?.charges]?.filter(Boolean),
      warrants: Array.isArray(record?.warrants) ? record?.warrants : [record?.warrants]?.filter(Boolean)
    }));
  }

  mapStatus(dbStatus) {
    const statusMap = {
      'IN_CUSTODY': 'IN_CUSTODY',
      'RELEASED': 'RELEASED',
      'DISMISSED': 'DISMISSED'
    };
    return statusMap?.[dbStatus] || 'UNKNOWN';
  }

  async handleFacilityInfo(claudeResponse = null) {
    try {
      const { phoneNumber } = await this.getDynamicPhoneData();
      const facilities = await inmateService?.getFacilities();
      const populationData = await inmateService?.getCurrentPopulation();

      // Use Claude's response if it provides facility information
      if (claudeResponse && typeof claudeResponse === 'string'&& claudeResponse?.toLowerCase()?.includes('facility')) {
        return {
          reply: claudeResponse,
          actions: [{ type: 'CALL', phone: phoneNumber }]
        };
      }

      let facilityInfo = "**Sacramento County Detention Facilities:**\n\n";

      if (facilities?.success && facilities?.data?.length > 0) {
        facilities?.data?.forEach(facility => {
          const population = populationData?.success
            ? populationData?.data?.find(p => p?.facility_id === facility?.facility_id)
            : null;

          facilityInfo += `**${facility?.facility_name}**\n`;
          facilityInfo += `Facility Code: ${facility?.facility_id}\n`;
          if (population) {
            facilityInfo += `Current Population: ${population?.current_count} inmates\n`;
          }
          facilityInfo += `\n`;
        });
      } else {
        // Fallback to static information if database unavailable
        facilityInfo = `**Sacramento County Detention Facilities:**

**Sacramento County Main Jail**
651 I Street, Sacramento, CA 95814
• Primary holding facility for Sacramento County
• Capacity: 4,000+ inmates  
• Visiting: Wed-Sun, 9 AM - 3 PM
• Phone: (916) 874-6750

**Rio Cosumnes Correctional Facility**
12500 Bruceville Road, Elk Grove, CA 95757
• Medium security correctional facility
• Capacity: 3,000+ inmates
• Visiting: Tue-Sun, 8 AM - 4 PM  
• Phone: (916) 874-6752

`;
      }

      facilityInfo += "I have real-time access to both facilities' booking databases. Would you like me to search for someone specific?";

      return {
        reply: facilityInfo,
        actions: [{ type: 'CALL', phone: phoneNumber }]
      };
    } catch (error) {
      return this.handleFacilityInfo(); // Fallback to static info
    }
  }

  // Keep session management methods for analytics only (not for limiting)
  async checkSearchSessionLimit(sessionId) {
    try {
      const { data, error } = await supabase
        ?.rpc('get_search_session_status', { p_session_id: sessionId });

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      // Return safe defaults on error - no limits enforced
      return {
        session_id: sessionId,
        search_count: 0,
        limit_reached: false,
        remaining_searches: 999999, // Unlimited
        should_redirect: false
      };
    }
  }

  async trackSearchSession(sessionId, searchQuery, searchResults) {
    try {
      const resultsData = Array.isArray(searchResults) ? searchResults : [];

      const { data, error } = await supabase
        ?.rpc('track_search_session', {
          p_session_id: sessionId,
          p_search_query: searchQuery,
          p_results_data: resultsData
        });

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      // Silent fail for session tracking - don't break search flow console.log('Session tracking error:', error);
      return null;
    }
  }

  async getSearchSessionStatus(sessionId) {
    try {
      const { data, error } = await supabase
        ?.rpc('get_search_session_status', { p_session_id: sessionId });

      if (error) {
        throw error;
      }

      return data;
    } catch (error) {
      return {
        session_id: sessionId,
        search_count: 0,
        limit_reached: false,
        remaining_searches: 999999 // Unlimited
      };
    }
  }

  formatSearchQuery(params) {
    const parts = [];
    if (params?.firstName) parts?.push(`First: ${params?.firstName}`);
    if (params?.lastName) parts?.push(`Last: ${params?.lastName}`);
    if (params?.dob) parts?.push(`DOB: ${params?.dob}`);
    if (params?.bookingNumber) parts?.push(`Booking: ${params?.bookingNumber}`);
    if (params?.facility) parts?.push(`Facility: ${params?.facility}`);

    return parts?.length > 0 ? parts?.join(', ') : 'General Search';
  }
}

// Export singleton instance
export const damianChat = new DamianChatService();
export default damianChat;